// Port of customer-apps/offering/offering-filter.service.ts: converts between the three shapes an
// offering filter takes — URL query params, the internal filter object and the API query params.
// The Angular service also injected a StorageService it never used; that dependency is dropped.
import { OLD_TO_NEW_SPORT_ID } from '@shared/offering-sport-id-mapping';
import type {
    LocationArea,
    LocationAreaField,
    OfferingFilterDateRange,
    OfferingFilters,
    OfferingFiltersApiQueryParams,
    OfferingFiltersQueryParams,
    OfferingFilterTimeRange,
    OfferingLocation,
} from '@shared/types/offering';

const FILTER_GROUPS_NOT_COUNTED = ['targetGroup', 'includePast', 'includeWaitListed', 'includeCanceled'];

export function countNonEmptyFilters(filters: OfferingFilters): number {
    return Object.keys(filters).filter((filterKey) => {
        if (FILTER_GROUPS_NOT_COUNTED.includes(filterKey)) {
            return false;
        }

        const filterValue = (filters as Record<string, unknown>)[filterKey];

        if (filterValue === null || filterValue === undefined || (Array.isArray(filterValue) && filterValue.length === 0)) {
            return false;
        }

        if (filterKey === 'onlyBooked' && filterValue === false) {
            return false;
        }

        return true;
    }).length;
}

export function filtersAreEmpty(filters: OfferingFilters): boolean {
    return countNonEmptyFilters(filters) === 0;
}

function asArray(value: string[] | string | number[] | number | undefined, name: string): string[] | undefined {
    if (typeof value === 'string') {
        return [value];
    }
    if (typeof value === 'number') {
        return [String(value)];
    }
    if (Array.isArray(value)) {
        return value.map(String);
    }
    console.warn(`Invalid data type for offering filter query param ${name}`, value);
    return undefined;
}

function setBooleanFilterForQueryParam(
    offeringFilters: OfferingFilters,
    queryParams: OfferingFiltersQueryParams,
    key: 'free' | 'includeCanceled' | 'includePast' | 'includeWaitListed',
): void {
    const value = queryParams[key];
    if (value === undefined) {
        return;
    }
    if (value === 'true') {
        offeringFilters[key] = true;
    } else if (value === 'false') {
        offeringFilters[key] = false;
    } else {
        console.warn('Invalid data type for offering filter query param', key, value);
    }
}

export function getLocationAreas(areaIds: string[], locations: OfferingLocation[]): LocationArea[] {
    return locations.reduce((areas: LocationArea[], location) => {
        location.areas.forEach((area) => {
            if (areaIds.includes(area.id)) {
                areas.push(area);
            }
        });
        return areas;
    }, []);
}

export function getLocationAreaFields(fieldIds: string[], locations: OfferingLocation[]): LocationAreaField[] {
    const fields: LocationAreaField[] = [];
    for (const location of locations) {
        for (const area of location.areas) {
            for (const field of area.fields) {
                if (fieldIds.includes(field.id)) {
                    fields.push(field);
                }
            }
        }
    }
    return fields;
}

export function queryParamsToOfferingFilters(
    queryParams: OfferingFiltersQueryParams,
    availableLocations?: OfferingLocation[],
): OfferingFilters {
    const offeringFilters: OfferingFilters = {};

    if ((queryParams.query?.trim().length ?? 0) > 0) {
        offeringFilters.query = queryParams.query!.trim();
    }

    if (queryParams.state) {
        offeringFilters.state = queryParams.state;
    }

    const categoryIds = queryParams.categoryIds ? asArray(queryParams.categoryIds, 'categoryIds') : undefined;
    if (categoryIds) {
        offeringFilters.categoryIds = [...categoryIds];
    }

    const sportIds = queryParams.sportIds ? asArray(queryParams.sportIds, 'sportsIds') : undefined;
    if (sportIds) {
        offeringFilters.sportIds = sportIds.map((sportId) => OLD_TO_NEW_SPORT_ID.get(sportId) ?? sportId);
    }

    const instructorIds = queryParams.instructorIds ? asArray(queryParams.instructorIds, 'instructorIds') : undefined;
    if (instructorIds) {
        offeringFilters.instructorIds = instructorIds.map((instructorId) => Number.parseInt(instructorId, 10));
    }

    if (queryParams.locationIds && (availableLocations?.length ?? 0) > 0) {
        const locationIds = asArray(queryParams.locationIds, 'locationIds') ?? [];
        offeringFilters.locations = availableLocations!.filter((location) => locationIds.includes(location.id));
    }

    if (queryParams.areaIds && (availableLocations?.length ?? 0) > 0) {
        const areaIds = asArray(queryParams.areaIds, 'areaIds') ?? [];
        offeringFilters.areas = getLocationAreas(areaIds, availableLocations!);
    }

    if (queryParams.fieldIds && (availableLocations?.length ?? 0) > 0) {
        const fieldIds = asArray(queryParams.fieldIds, 'fieldIds') ?? [];
        offeringFilters.fields = getLocationAreaFields(fieldIds, availableLocations!);
    }

    const weekdays = queryParams.weekdays ? asArray(queryParams.weekdays, 'weekdays') : undefined;
    if (weekdays) {
        offeringFilters.weekdays = [...weekdays];
    }

    setBooleanFilterForQueryParam(offeringFilters, queryParams, 'free');
    setBooleanFilterForQueryParam(offeringFilters, queryParams, 'includeCanceled');
    setBooleanFilterForQueryParam(offeringFilters, queryParams, 'includePast');
    setBooleanFilterForQueryParam(offeringFilters, queryParams, 'includeWaitListed');

    if (queryParams.executionType !== undefined) {
        if (queryParams.executionType === 'presence' || queryParams.executionType === 'online') {
            offeringFilters.executionType = queryParams.executionType;
        } else {
            console.warn('Invalid data type for offering filter query param executionType', queryParams.executionType);
        }
    }

    if (queryParams.startDateRange !== undefined) {
        try {
            offeringFilters.startDateRange = JSON.parse(queryParams.startDateRange) as OfferingFilterDateRange;
        } catch (e) {
            console.warn('Invalid data type for offering filter query param startDateRange', queryParams.startDateRange, e);
        }
    }

    if (queryParams.startTimeRange !== undefined) {
        try {
            offeringFilters.startTimeRange = JSON.parse(queryParams.startTimeRange) as OfferingFilterTimeRange;
        } catch (e) {
            console.warn('Invalid data type for offering filter query param startTimeRange', queryParams.startTimeRange, e);
        }
    }

    if (queryParams.gender !== undefined) {
        if (queryParams.gender === 'male' || queryParams.gender === 'female') {
            offeringFilters.gender = queryParams.gender;
        } else {
            console.warn('Invalid data type for offering filter query param gender', queryParams.gender);
        }
    }

    if (queryParams.age !== undefined) {
        const age = Number.parseInt(queryParams.age, 10);
        if (Number.isNaN(age) === false) {
            offeringFilters.age = age;
        } else {
            console.warn('Invalid data type for offering filter query param age', queryParams.age);
        }
    }

    if (queryParams.targetGroup !== undefined) {
        if (queryParams.targetGroup === 'all') {
            // no filters required
        } else if (queryParams.targetGroup === 'guest' || queryParams.targetGroup === 'member') {
            offeringFilters.targetGroup = queryParams.targetGroup;
        } else {
            console.warn('Invalid data type for offering filter query param targetGroup', queryParams.targetGroup);
        }
    }

    return offeringFilters;
}

/**
 * Filters as they are written back into the URL: location/area/field objects collapse to their ids,
 * the two ranges are JSON encoded. Keys whose value is `undefined` are dropped by vue-router, which
 * is what Angular's `queryParamsHandling: 'merge'` with `undefined` values does too.
 */
export function offeringFiltersToRouterParams(offeringFilters: OfferingFilters): Record<string, unknown> {
    return {
        ...offeringFilters,
        locations: undefined,
        locationIds: offeringFilters.locations?.map((location) => location.id),
        areas: undefined,
        areaIds: offeringFilters.areas?.map((area) => area.id),
        fields: undefined,
        fieldIds: offeringFilters.fields?.map((field) => field.id),
        startDateRange: offeringFilters.startDateRange ? JSON.stringify(offeringFilters.startDateRange) : undefined,
        startTimeRange: offeringFilters.startTimeRange ? JSON.stringify(offeringFilters.startTimeRange) : undefined,
    };
}

export function offeringFiltersToApiQueryParams(offeringFilters: OfferingFilters): OfferingFiltersApiQueryParams {
    const params: OfferingFiltersApiQueryParams = {};

    if (offeringFilters.query) {
        params['filters[query]'] = [offeringFilters.query];
    }

    if (offeringFilters.state === 'onlyBooked') {
        params['filters[onlyBooked]'] = 1;
    } else if (offeringFilters.state === 'onlyFavorites') {
        params['filters[isFavorite]'] = 1;
    }

    if (offeringFilters.teamIds) {
        params['filters[teamIds][]'] = [...offeringFilters.teamIds];
    }
    if (offeringFilters.categoryIds) {
        params['filters[categoryIds][]'] = [...offeringFilters.categoryIds];
    }
    if (offeringFilters.sportIds) {
        params['filters[sportIds][]'] = [...offeringFilters.sportIds];
    }
    if (offeringFilters.departmentIds) {
        params['filters[departmentIds][]'] = [...offeringFilters.departmentIds];
    }
    if (offeringFilters.instructorIds) {
        params['filters[instructorIds][]'] = [...offeringFilters.instructorIds];
    }
    if (offeringFilters.weekdays) {
        params['filters[weekdays][]'] = [...offeringFilters.weekdays];
    }

    if (offeringFilters.executionType === 'presence') {
        params['filters[executionTypes][]'] = ['presence', 'hybrid'];
    } else if (offeringFilters.executionType === 'online') {
        params['filters[executionTypes][]'] = ['online', 'hybrid'];
    }

    if (offeringFilters.startDateRange?.start) {
        params.start = offeringFilters.startDateRange.start;
    }
    if (offeringFilters.startDateRange?.end) {
        params.end = offeringFilters.startDateRange.end;
    }

    if (offeringFilters.startTimeRange?.start) {
        params['filters[startTimeRange][start][hours]'] = offeringFilters.startTimeRange.start.hours!;
        params['filters[startTimeRange][start][minutes]'] = offeringFilters.startTimeRange.start.minutes!;
        if (offeringFilters.startTimeRange.end) {
            params['filters[startTimeRange][end][hours]'] = offeringFilters.startTimeRange.end.hours!;
            params['filters[startTimeRange][end][minutes]'] = offeringFilters.startTimeRange.end.minutes!;
        }
    }

    if (offeringFilters.includeCanceled === true) {
        params['filters[includeCanceled]'] = 1;
    }
    if (offeringFilters.includePast === true) {
        params['filters[includePast]'] = 1;
    }

    if (offeringFilters.includeWaitListed === true) {
        params['filters[includeWaitListed]'] = 1;
    } else if (offeringFilters.includeWaitListed === false) {
        params['filters[includeWaitListed]'] = 0;
    }

    if (offeringFilters.free === true) {
        params['filters[free]'] = 1;
    } else if (offeringFilters.free === false) {
        params['filters[free]'] = 0;
    }

    if (offeringFilters.gender === 'male') {
        params['filters[isBookableForMen]'] = 1;
    } else if (offeringFilters.gender === 'female') {
        params['filters[isBookableForWomen]'] = 1;
    }

    if (typeof offeringFilters.age === 'number') {
        params['filters[forAge]'] = offeringFilters.age;
    }

    if (offeringFilters.targetGroup === 'guest') {
        params['filters[isBookableForGuestMembers]'] = 1;
    } else if (offeringFilters.targetGroup === 'member') {
        params['filters[isBookableForActiveOrPassiveMembers]'] = 1;
    }

    if (offeringFilters.locations) {
        const locationsWithoutSelectedSubAreasOrFields = offeringFilters.locations.filter(
            (location) => offeringFilters.areas?.find((area) => area.locationId === location.id) === undefined,
        );
        if (locationsWithoutSelectedSubAreasOrFields.length > 0) {
            params['filters[locationIds][]'] = locationsWithoutSelectedSubAreasOrFields.map((location) => location.id);
        }
    }

    if (offeringFilters.areas) {
        const areasWithoutSelectedFields = offeringFilters.areas.filter(
            (area) => offeringFilters.fields?.find((field) => field.areaId === area.id) === undefined,
        );
        if (areasWithoutSelectedFields.length > 0) {
            params['filters[areaIds][]'] = areasWithoutSelectedFields.map((area) => area.id);
        }
    }

    if (offeringFilters.fields) {
        params['filters[fieldIds][]'] = offeringFilters.fields.map((field) => field.id);
    }

    return params;
}

/** Serializes API query params the way Angular's HttpParams does: array values repeat their key. */
export function toQueryString(params: Record<string, string | number | (string | number)[] | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined) {
            continue;
        }
        if (Array.isArray(value)) {
            value.forEach((entry) => search.append(key, String(entry)));
        } else {
            search.append(key, String(value));
        }
    }
    return search.toString();
}
